import { useCallback, useRef, useState, type ReactNode } from 'react';

// Shared resize wrapper used by the Media + Embed node views.
// - children fill 100% of the wrapper; the wrapper owns the width
// - drag the bottom-right handle for pixel resize; clamps to [MIN, parent]
// - preset pills (S/M/L/Full) on hover/drag for one-click common sizes
// - Full = null width (children stretch to column)
//
// Editable=false hides all chrome so shared/read-only views stay clean.

export type WrapPreset = { label: string; width: number | null };

const DEFAULT_PRESETS: WrapPreset[] = [
  { label: 'S', width: 240 },
  { label: 'M', width: 420 },
  { label: 'L', width: 680 },
  { label: 'Full', width: null },
];

const MIN_WIDTH = 160;

export function ResizableWrap({
  width,
  onResize,
  editable,
  className,
  presets = DEFAULT_PRESETS,
  children,
}: {
  width: number | null;
  onResize: (width: number | null) => void;
  editable: boolean;
  className?: string;
  presets?: WrapPreset[];
  children: ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);

  const startResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!editable) return;
      e.preventDefault();
      e.stopPropagation();
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const startRect = wrapper.getBoundingClientRect();
      const startX = e.clientX;
      const startW = startRect.width;
      setDragging(true);

      const onMove = (ev: PointerEvent) => {
        const next = Math.max(MIN_WIDTH, startW + (ev.clientX - startX));
        const parent = wrapper.parentElement;
        const maxW = parent ? parent.clientWidth : next;
        wrapper.style.width = `${Math.min(next, maxW)}px`;
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        setDragging(false);
        const finalW = Math.max(
          MIN_WIDTH,
          Math.round(startW + (ev.clientX - startX)),
        );
        const parent = wrapper.parentElement;
        const maxW = parent ? parent.clientWidth : finalW;
        onResize(Math.min(finalW, maxW));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [editable, onResize],
  );

  const style: React.CSSProperties = width
    ? { width, maxWidth: '100%', position: 'relative' }
    : { width: '100%', position: 'relative' };

  const showChrome = editable && (hover || dragging);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {showChrome && (
        <>
          <div className="resizable-presets">
            {presets.map((p) => {
              const active =
                (p.width === null && width === null) ||
                (p.width !== null && width === p.width);
              return (
                <button
                  key={p.label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onResize(p.width);
                  }}
                  className={active ? 'active' : ''}
                  type="button"
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div
            className="resizable-handle"
            onPointerDown={startResize}
            aria-label="Resize"
          />
        </>
      )}
    </div>
  );
}
